import type { OnboardingStepProps } from '../OnboardingWizard';
import {
  ArrowBack,
  Campaign,
  LinkedIn,
  Person,
  Search,
  WarningAmber,
} from '@mui/icons-material';
import { Button, FormControl, FormLabel, TextField } from '@mui/material';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { getStoredUTMData } from '@shared/utils/utmTracking';
import { OnboardingOptionItem } from './OnboardingOptionItem';

interface AcquisitionSource {
  icon: React.ReactElement;
  label: string;
  value: string;
}

const acquisitionSources: AcquisitionSource[] = [
  { icon: <Search color="primary" />, label: 'Google Search', value: 'google_search' },
  { icon: <LinkedIn color="primary" />, label: 'LinkedIn - Feed / Post', value: 'linkedin_feed' },
  { icon: <LinkedIn color="primary" />, label: 'LinkedIn - Message', value: 'linkedin_message' },
  { icon: <Person color="primary" />, label: 'Referral from a colleague or friend', value: 'referral' },
  { icon: <Campaign color="primary" />, label: 'Advertisement (Google, LinkedIn, etc.)', value: 'advertisement' },
  { icon: <WarningAmber color="primary" />, label: 'Other', value: 'other' },
];

function detectAcquisitionSource(): { detectedSource: string; detectedDetails?: string } {
  const utmData = getStoredUTMData();

  if (utmData?.utmSource) {
    const source = utmData.utmSource.toLowerCase();
    if (source.includes('google') || source === 'google_search' || source === 'cpc') {
      return { detectedSource: 'google_search' };
    }
    if (source.includes('linkedin')) {
      return { detectedSource: 'linkedin' };
    }
    if (source.includes('facebook') || source.includes('twitter') || source.includes('instagram')) {
      return { detectedSource: 'social_media' };
    }
    if (source.includes('youtube')) {
      return { detectedSource: 'youtube_podcast' };
    }
  }

  if (utmData?.utmMedium) {
    const medium = utmData.utmMedium.toLowerCase();
    if (medium.includes('cpc') || medium.includes('ppc') || medium.includes('paid') || medium.includes('ad')) {
      return { detectedSource: 'advertisement' };
    }
    if (medium.includes('social')) {
      return { detectedSource: 'social_media' };
    }
  }

  if (utmData?.referrerCode) {
    return { detectedSource: 'referral', detectedDetails: `Referral code: ${utmData.referrerCode}` };
  }

  if (utmData?.referrer && !utmData.referrer.includes(window.location.hostname)) {
    const referrer = utmData.referrer.toLowerCase();
    if (referrer.includes('google.com')) return { detectedSource: 'google_search' };
    if (referrer.includes('linkedin.com')) return { detectedSource: 'linkedin' };
    if (referrer.includes('facebook.com') || referrer.includes('twitter.com') || referrer.includes('instagram.com')) {
      return { detectedSource: 'social_media' };
    }
    if (referrer.includes('youtube.com')) return { detectedSource: 'youtube_podcast' };
    return { detectedSource: 'other', detectedDetails: `From: ${utmData.referrer}` };
  }

  return { detectedSource: '' };
}

export const AcquisitionSourceStep: React.FC<OnboardingStepProps> = ({ onNext, onBack, onSkip }) => {
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [otherDetails, setOtherDetails] = useState<string>('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [wasAutoDetected, setWasAutoDetected] = useState(false);

  useEffect(() => {
    const { detectedSource, detectedDetails } = detectAcquisitionSource();
    if (detectedSource) {
      setSelectedSource(detectedSource);
      setWasAutoDetected(true);
      if (detectedSource === 'other' && detectedDetails) {
        setOtherDetails(detectedDetails);
        setShowOtherInput(true);
      }
    }
  }, []);

  const handleSourceSelect = (value: string) => {
    setSelectedSource(value);
    setShowOtherInput(value === 'other');
    if (value !== selectedSource) setWasAutoDetected(false);
  };

  const handleContinue = () => {
    const sourceData = {
      source: selectedSource,
      ...(selectedSource === 'other' && otherDetails && { sourceDetails: otherDetails }),
    };
    sessionStorage.setItem('acquisitionSource', JSON.stringify(sourceData));
    onNext();
  };

  return (
    <div className="onboarding-step" data-testid="acquisition-source-step">
      <div className="onboarding-step__content">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: 'easeOut' }}>
          <h4 className="onboarding-step__section-title">How did you hear about us?</h4>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}>
          <p className="text-secondary mb-16 leading-relaxed" style={{ maxWidth: 700 }}>
            {wasAutoDetected
              ? "We detected how you arrived! Please confirm or select a different option if this isn't quite right."
              : 'Help us understand how you discovered Kandir so we can continue to improve and reach more people like you.'}
          </p>
          {wasAutoDetected && (
            <div className="bg-success-light text-success px-16 py-8 rounded-md mb-24" style={{ display: 'inline-block' }}>
              <span className="font-medium text-sm">✨ Auto-detected from your visit</span>
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6, ease: 'easeOut' }}>
          <div className="onboarding-use-cases__list">
            {acquisitionSources.map((source) => (
              <OnboardingOptionItem
                key={source.value}
                isSelected={selectedSource === source.value}
                onClick={() => handleSourceSelect(source.value)}
                icon={source.icon}
                label={source.label}
              />
            ))}
          </div>

          {showOtherInput && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.3 }}>
              <FormControl fullWidth className="mt-16" style={{ maxWidth: 800 }}>
                <FormLabel className="mb-8 font-semibold">Please tell us more</FormLabel>
                <TextField
                  data-testid="other-source-input"
                  placeholder="Where did you hear about Kandir?"
                  value={otherDetails}
                  onChange={(e) => setOtherDetails(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </FormControl>
            </motion.div>
          )}
        </motion.div>
      </div>

      <div className="onboarding-step__actions">
        {onBack ? (
          <Button onClick={onBack} startIcon={<ArrowBack />} color="inherit">Back</Button>
        ) : <span />}
        <div className="onboarding-step__actions-right">
          <Button onClick={onSkip} variant="text" color="inherit" className="onboarding-btn--skip">Skip</Button>
          <Button
            data-testid="acquisition-source-continue-button"
            onClick={handleContinue}
            disabled={!selectedSource}
            variant="contained"
            className="onboarding-btn--cta"
          >
            Finish
          </Button>
        </div>
      </div>
    </div>
  );
};
